import { IPreferredStaffItem } from "@/api_framework/api_modals/Configuration";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useClinicListAPI } from "@/app/hooks/api_hooks/org/useClinicListAPI";
import { usePreferredStaffListAPI } from "@/app/hooks/api_hooks/configuration/usePreferredStaffListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BiEdit } from "react-icons/bi";
import { MdSearch } from "react-icons/md";
import PreferredDoctorModal from "./PreferredDoctorModal";

const PreferredDoctorMapping = () => {
  const { NO_APPOINTMENTS } = useImageConstants();
  const { execute: fetchMappings } = usePreferredStaffListAPI();
  const { execute: fetchClinics, clinicList } = useClinicListAPI();

  const [rows, setRows] = useState<IPreferredStaffItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedRow, setSelectedRow] = useState<IPreferredStaffItem | null>(null);

  const loadMappings = useCallback(async () => {
    setIsLoading(true);
    const data = await fetchMappings();
    setRows(data);
    setIsLoading(false);
  }, [fetchMappings]);

  useEffect(() => {
    loadMappings();
    fetchClinics();
  }, [loadMappings, fetchClinics]);

  const openCreate = () => {
    setModalMode("create");
    setSelectedRow(null);
    setModalOpen(true);
  };

  const openEdit = (row: IPreferredStaffItem) => {
    setModalMode("edit");
    setSelectedRow(row);
    setModalOpen(true);
  };

  // The org clinic-list API returns items keyed by `id`; the modal keys off
  // `clinic_id`. Normalize here so the picker's option values line up.
  const clinics = useMemo(
    () =>
      (clinicList ?? []).map((c: { id?: string; clinic_id?: string; clinic_name?: string }) => ({
        clinic_id: c.id ?? c.clinic_id ?? "",
        clinic_name: c.clinic_name ?? "",
      })),
    [clinicList],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.clinic_name?.toLowerCase().includes(q) ||
        r.clinic_id?.toLowerCase().includes(q) ||
        (r.staffs ?? []).some(
          (s) => s.doctor_name?.toLowerCase().includes(q) || s.id?.toLowerCase().includes(q),
        ),
    );
  }, [rows, searchQuery]);

  const columns = [
    { header: "SN", cell: (_: IPreferredStaffItem, index: number) => index + 1 },
    { header: "Clinic", cell: (row: IPreferredStaffItem) => row.clinic_name || row.clinic_id },
    {
      header: "Preferred Doctors (in order)",
      cell: (row: IPreferredStaffItem) => (
        <div className="flex flex-wrap gap-1.5">
          {(row.staffs ?? []).map((s, index) => (
            <span
              key={s.id}
              className={
                s.status === "Inactive"
                  ? "rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-400 line-through"
                  : "rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"
              }
            >
              {index + 1}. {s.doctor_name || s.id}
            </span>
          ))}
          {(row.staffs ?? []).length === 0 && <span className="text-xs text-slate-400">—</span>}
        </div>
      ),
    },
    {
      header: "Action",
      cell: (row: IPreferredStaffItem) => (
        <button
          type="button"
          onClick={() => openEdit(row)}
          className="inline-flex h-8 items-center justify-center gap-2 rounded-xl bg-slate-100 px-3 text-slate-700 transition hover:bg-slate-200"
        >
          <BiEdit className="h-4 w-4" />
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="px-5 py-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Preferred Doctors</h2>
          <p className="mt-1 text-sm text-slate-500">Rank a clinic's preferred doctors (1 shows first)</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-900"
        >
          + Add Mapping
        </button>
      </div>

      <div className="mt-6 flex w-full flex-col gap-2 sm:max-w-xl">
        <div className="relative w-full">
          <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search clinic or doctor"
            className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-10 text-sm text-slate-900 shadow-sm focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
          {searchQuery && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              onClick={() => setSearchQuery("")}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <>
            <SkeletonType1 />
            <SkeletonType1 className="mt-4" />
          </>
        ) : filteredRows.length === 0 ? (
          <NoDataFoundComponent
            imageURL={NO_APPOINTMENTS}
            message="No Preferred Doctors"
            description="Add a mapping to rank a clinic's preferred doctors."
          />
        ) : (
          <DataTable data={filteredRows} columns={columns} maxHeight="600px" />
        )}
      </div>

      {modalOpen && (
        <PreferredDoctorModal
          open={modalOpen}
          mode={modalMode}
          clinicList={clinics}
          initial={selectedRow}
          onClose={() => setModalOpen(false)}
          onSaved={loadMappings}
        />
      )}
    </div>
  );
};

export default PreferredDoctorMapping;
